import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { messageFor } from '../lib/session';
import { Field, Loading, Notice } from '../components/ui';

export type BillingAddress = {
  legal_name: string;
  billing_email: string;
  primary_contact_name: string | null;
  primary_contact_phone: string | null;
  billing_address_line1: string | null;
  billing_address_line2: string | null;
  billing_city: string | null;
  billing_state: string | null;
  billing_postal_code: string | null;
  billing_country: string | null;
};

type Payload = { address: BillingAddress; complete: boolean };

/**
 * Where a customer keeps the address that prints on their invoices.
 *
 * Deliberately theirs to fill in rather than something staff transcribe from
 * an email: they are the ones who know it, and they are the ones who have to
 * live with it being wrong on a receipt their accountant reads.
 */
export function BillingDetails({ canEdit, onSaved }: { canEdit: boolean; onSaved?: () => void }) {
  const navigate = useNavigate();
  const [address, setAddress] = useState<BillingAddress | null>(null);
  const [complete, setComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void api.get<Payload>('/api/portal/billing-address')
      .then((result) => { setAddress(result.address); setComplete(result.complete); })
      .catch((cause) => setError(messageFor(cause)));
  }, []);

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const result = await api.put<Payload>('/api/portal/billing-address', {
        addressLine1: form.get('addressLine1'),
        addressLine2: form.get('addressLine2'),
        city: form.get('city'),
        state: form.get('state'),
        postalCode: form.get('postalCode'),
        country: String(form.get('country') || 'US').toUpperCase(),
        contactPhone: form.get('contactPhone'),
      });
      setAddress(result.address);
      setComplete(result.complete);
      setSaved(true);
      // The banner that sent them here reads from the session, so it has to be
      // re-fetched or it keeps asking for something already given.
      onSaved?.();
    } catch (cause) {
      setError(messageFor(cause));
    } finally {
      setBusy(false);
    }
  }

  if (error && !address) return <Notice kind="error">{error}</Notice>;
  if (!address) return <Loading />;

  return (
    <>
      <header className="simple-page-head">
        <div>
          <span>Billing details</span>
          <h1>{address.legal_name}</h1>
          <p>This address appears on every invoice and receipt we issue you.</p>
        </div>
      </header>

      <div className="simple-panel">
        {error && <Notice kind="error">{error}</Notice>}
        {saved && <Notice kind="ok">Billing address saved. It will be used on your next invoice.</Notice>}
        {!complete && !saved && (
          <Notice kind="warn">
            We do not have a complete billing address for you yet. Please add it so your invoices are correct.
          </Notice>
        )}

        {!canEdit ? (
          <Notice kind="info">
            Your access does not include billing. Ask the account owner at {address.legal_name} to add the
            billing address, or contact your account manager.
          </Notice>
        ) : (
          <form onSubmit={save}>
            <Field label="Street address">
              <input name="addressLine1" defaultValue={address.billing_address_line1 ?? ''}
                     autoComplete="address-line1" maxLength={200} required />
            </Field>
            <Field label="Apartment, suite, unit" hint="Optional.">
              <input name="addressLine2" defaultValue={address.billing_address_line2 ?? ''}
                     autoComplete="address-line2" maxLength={200} />
            </Field>
            <div className="simple-form-grid pair">
              <Field label="City">
                <input name="city" defaultValue={address.billing_city ?? ''}
                       autoComplete="address-level2" maxLength={120} required />
              </Field>
              <Field label="State or province">
                <input name="state" defaultValue={address.billing_state ?? ''}
                       autoComplete="address-level1" maxLength={120} required />
              </Field>
            </div>
            <div className="simple-form-grid pair">
              <Field label="Postal code">
                <input name="postalCode" defaultValue={address.billing_postal_code ?? ''}
                       autoComplete="postal-code" maxLength={32} required />
              </Field>
              <Field label="Country" hint="Two-letter code, such as US.">
                <input name="country" defaultValue={address.billing_country || 'US'}
                       autoComplete="country" maxLength={2} minLength={2} required />
              </Field>
            </div>
            <Field label="Billing phone" hint="Optional. Used if we need to reach you about a payment.">
              <input name="contactPhone" type="tel" defaultValue={address.primary_contact_phone ?? ''}
                     autoComplete="tel" maxLength={50} />
            </Field>
            <div className="form-actions">
              <button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save billing address'}</button>
              <button type="button" className="secondary" onClick={() => navigate('/portal')}>
                {saved ? 'Back to projects' : 'Cancel'}
              </button>
            </div>
          </form>
        )}

        <dl className="billing-summary">
          <div><dt>Invoices are sent to</dt><dd>{address.billing_email}</dd></div>
          {address.primary_contact_name && (
            <div><dt>Attention</dt><dd>{address.primary_contact_name}</dd></div>
          )}
        </dl>
        <p className="muted-note">
          To change your company name or the address invoices are emailed to, contact your account manager.
        </p>
      </div>
    </>
  );
}
